"use client";

import { useCallback, useEffect, useState } from "react";

function readValue<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const item = window.localStorage.getItem(key);
    return item ? (JSON.parse(item) as T) : fallback;
  } catch {
    return fallback;
  }
}

/**
 * SSR-safe localStorage state. Always initializes with `fallback` so the
 * server HTML and the client's first paint match (avoids hydration errors
 * from reading window.localStorage during useState init).
 *
 * The third return value is `true` after the client has read localStorage,
 * so callers can avoid acting on the temporary fallback (e.g. API fetches).
 */
export function useLocalStorage<T>(
  key: string,
  fallback: T,
): [T, (value: T | ((prev: T) => T)) => void, boolean] {
  const [value, setValue] = useState<T>(fallback);
  const [hasHydrated, setHasHydrated] = useState(false);

  useEffect(() => {
    setValue(readValue(key, fallback));
    setHasHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const setStoredValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      setValue((prev) => {
        const resolved = next instanceof Function ? next(prev) : next;
        if (typeof window !== "undefined") {
          try {
            window.localStorage.setItem(key, JSON.stringify(resolved));
          } catch {
            // localStorage may be unavailable (private browsing, quota) — ignore.
          }
        }
        return resolved;
      });
    },
    [key],
  );

  return [value, setStoredValue, hasHydrated];
}
